// 2D canvas editor for ClayForge Pen tool. Renders a Pen path with anchor/control
// handles over a grid + optional reference photo. Drives `app.vaseParams.penProfile`.
//
// Coordinates: world = (z, r) in mm. z is axis (vertical, growing UP). r is radius
// (horizontal, growing right from the axis line at center). Screen = pixels.
// We render z increasing UPWARD (so the vase looks right-side-up): screenY = h - margin - z*scale.

import type { AnchorContinuity, AnchorKind, PenAnchor, PenPath, Vec2 } from '../core/penTool';

export interface ViewTransform {
  scale: number;       // px per mm
  offsetX: number;     // px — center of axis (r=0)
  offsetY: number;     // px — y of z=0 (bottom)
}

export type AnchorHit =
  | { kind: 'anchor'; index: number }
  | { kind: 'cp1'; index: number }
  | { kind: 'cp2'; index: number }
  | { kind: 'arcMid'; index: number }
  | null;

export const HANDLE_HIT_RADIUS = 8; // px

export function worldToScreen(p: Vec2, t: ViewTransform, h: number): { x: number; y: number } {
  return {
    x: t.offsetX + p.r * t.scale,
    y: h - t.offsetY - p.z * t.scale
  };
}

export function screenToWorld(x: number, y: number, t: ViewTransform, h: number): Vec2 {
  return {
    r: (x - t.offsetX) / t.scale,
    z: (h - t.offsetY - y) / t.scale
  };
}

export function fitView(path: PenPath, w: number, h: number, padding = 40): ViewTransform {
  let zMin = 0, zMax = 100, rMin = 0, rMax = 50;
  if (path.anchors.length > 0) {
    zMin = Infinity; zMax = -Infinity; rMin = Infinity; rMax = -Infinity;
    for (const a of path.anchors) {
      zMin = Math.min(zMin, a.z); zMax = Math.max(zMax, a.z);
      rMin = Math.min(rMin, a.r); rMax = Math.max(rMax, a.r);
    }
    rMin = Math.min(rMin, 0); // always show axis line
  }
  const zSpan = Math.max(10, zMax - zMin);
  const rSpan = Math.max(10, rMax - rMin);
  const scaleZ = (h - padding * 2) / zSpan;
  const scaleR = (w - padding * 2) / Math.max(rSpan, 60);
  const scale = Math.min(scaleZ, scaleR);
  return {
    scale,
    offsetX: padding + (-rMin) * scale,
    offsetY: padding + (-zMin) * scale
  };
}

export function hitTest(
  x: number,
  y: number,
  path: PenPath,
  t: ViewTransform,
  h: number,
  selectedIndex: number | null
): AnchorHit {
  // Control points (only for currently-selected anchor) take priority over anchors.
  if (selectedIndex !== null && selectedIndex >= 0 && selectedIndex < path.anchors.length) {
    const a = path.anchors[selectedIndex];
    if (a.kind === 'cubic' || a.kind === 'quad') {
      if (a.cp1) {
        const s = worldToScreen(a.cp1, t, h);
        if (Math.hypot(s.x - x, s.y - y) <= HANDLE_HIT_RADIUS) return { kind: 'cp1', index: selectedIndex };
      }
      if (a.kind === 'cubic' && a.cp2) {
        const s = worldToScreen(a.cp2, t, h);
        if (Math.hypot(s.x - x, s.y - y) <= HANDLE_HIT_RADIUS) return { kind: 'cp2', index: selectedIndex };
      }
    }
    if (a.kind === 'arc' && a.arcMid) {
      const s = worldToScreen(a.arcMid, t, h);
      if (Math.hypot(s.x - x, s.y - y) <= HANDLE_HIT_RADIUS) return { kind: 'arcMid', index: selectedIndex };
    }
  }
  for (let i = 0; i < path.anchors.length; i++) {
    const a = path.anchors[i];
    const s = worldToScreen({ z: a.z, r: a.r }, t, h);
    if (Math.hypot(s.x - x, s.y - y) <= HANDLE_HIT_RADIUS) return { kind: 'anchor', index: i };
  }
  return null;
}

/** Snap world coordinate to grid (mm). step = grid spacing in mm. */
export function snapToGrid(p: Vec2, step: number): Vec2 {
  return { z: Math.round(p.z / step) * step, r: Math.round(p.r / step) * step };
}

/** Re-tie cubic continuity: when an anchor's incoming handle (cp2) moves,
 *  realign the next anchor's outgoing handle (cp1) per its continuity flag. */
export function applyContinuity(path: PenPath, idx: number): void {
  if (idx < 0 || idx >= path.anchors.length - 1) return;
  const cur = path.anchors[idx];
  const next = path.anchors[idx + 1];
  if (cur.kind !== 'cubic' || next.kind !== 'cubic') return;
  if (!cur.cp2 || !next.cp1) return;
  const c = cur.continuity ?? 'smooth';
  if (c === 'cusp') return;
  const incoming = { z: cur.z - cur.cp2.z, r: cur.r - cur.cp2.r };
  const len = Math.hypot(incoming.z, incoming.r);
  if (len < 1e-6) return;
  if (c === 'symmetric') {
    next.cp1 = { z: cur.z + incoming.z, r: cur.r + incoming.r };
  } else {
    // smooth: keep the existing length of next.cp1, just realign direction
    const cur1Len = Math.hypot(next.cp1.z - cur.z, next.cp1.r - cur.r);
    const ux = incoming.z / len, uy = incoming.r / len;
    next.cp1 = { z: cur.z + ux * cur1Len, r: cur.r + uy * cur1Len };
  }
}

/** Cycle continuity: cusp → smooth → symmetric → cusp. */
export function cycleContinuity(c: AnchorContinuity | undefined): AnchorContinuity {
  if (c === 'cusp') return 'smooth';
  if (c === 'smooth') return 'symmetric';
  return 'cusp';
}

export type { AnchorContinuity, AnchorKind, PenAnchor, PenPath, Vec2 };
